import { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Users, Plus, Search, Filter, ChevronDown, 
  Check, X, UserPlus, MapPin, MoreVertical, Eye, Edit, Trash2,
  CheckCircle, XCircle, AlertCircle, RefreshCcw, Download, Link2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '@/components/ui/dialog';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminWorkshopAttendance = () => {
  const [sessions, setSessions] = useState([]);
  const [workshopTopics, setWorkshopTopics] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSession, setSelectedSession] = useState(null);
  const [attendees, setAttendees] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const [showAddAttendeeModal, setShowAddAttendeeModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  
  // Form states
  const [sessionForm, setSessionForm] = useState({
    workshop_topic_id: '',
    title: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    facilitator: '',
    location: '',
    is_virtual: true,
    meeting_link: '',
    max_attendees: '',
    notes: ''
  });
  
  const [attendeeForm, setAttendeeForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: ''
  });

  const getToken = () => localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      
      const [sessionsRes, statsRes, topicsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/attendance/sessions`, { headers, params }),
        axios.get(`${API_URL}/api/admin/attendance/reports/summary`, { headers }),
        axios.get(`${API_URL}/api/workshop-topics`, { headers })
      ]);
      
      setSessions(sessionsRes.data || []);
      setStats(statsRes.data);
      setWorkshopTopics(topicsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (sessionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/attendance/sessions/${sessionId}`, { headers });
      setAttendees(res.data.attendees || []);
      setSelectedSession(res.data);
    } catch (error) {
      toast.error('Failed to load attendees');
    }
  };

  const handleCreateSession = async () => {
    try {
      if (!sessionForm.title || !sessionForm.scheduled_date) {
        toast.error('Please fill in required fields');
        return;
      }
      
      const payload = {
        ...sessionForm,
        max_attendees: sessionForm.max_attendees ? parseInt(sessionForm.max_attendees) : null
      };
      
      await axios.post(`${API_URL}/api/admin/attendance/sessions`, payload, { headers });
      
      toast.success('Workshop session scheduled');
      setShowCreateModal(false);
      resetSessionForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create session');
    }
  };

  const handleUpdateSession = async () => {
    try {
      const payload = {
        ...sessionForm,
        max_attendees: sessionForm.max_attendees ? parseInt(sessionForm.max_attendees) : null
      };
      
      await axios.put(`${API_URL}/api/admin/attendance/sessions/${editingSession.id}`, payload, { headers });
      
      toast.success('Session updated');
      setEditingSession(null);
      setShowCreateModal(false);
      resetSessionForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const handleDeleteSession = async (id) => {
    if (!confirm('Delete this session and all attendance records?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/attendance/sessions/${id}`, { headers });
      toast.success('Session deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleCompleteSession = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/attendance/sessions/${id}/complete`, {}, { headers });
      toast.success('Session marked as completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCancelSession = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/attendance/sessions/${id}/cancel`, {}, { headers });
      toast.success('Session cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel session');
    }
  };

  const handleAddAttendee = async () => {
    try {
      if (!attendeeForm.name || !attendeeForm.email) {
        toast.error('Name and email are required');
        return;
      }
      
      await axios.post(`${API_URL}/api/admin/attendance/sessions/${selectedSession.id}/attendees`, attendeeForm, { headers });
      toast.success('Attendee added');
      setShowAddAttendeeModal(false);
      setAttendeeForm({ name: '', email: '', phone: '', organization: '' });
      fetchAttendees(selectedSession.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add attendee');
    }
  };

  const handleCheckIn = async (sessionId, attendeeId) => {
    try {
      await axios.post(`${API_URL}/api/admin/attendance/sessions/${sessionId}/check-in/${attendeeId}`, {}, { headers });
      toast.success('Attendee checked in');
      fetchAttendees(sessionId);
    } catch (error) {
      toast.error('Failed to check in attendee');
    }
  };

  const handleRemoveAttendee = async (sessionId, attendeeId) => {
    if (!confirm('Remove this attendee?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/attendance/sessions/${sessionId}/attendees/${attendeeId}`, { headers });
      toast.success('Attendee removed');
      fetchAttendees(sessionId);
    } catch (error) {
      toast.error('Failed to remove attendee');
    }
  };

  const resetSessionForm = () => {
    setSessionForm({
      workshop_topic_id: '',
      title: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 60,
      facilitator: '',
      location: '',
      is_virtual: true,
      meeting_link: '',
      max_attendees: '',
      notes: ''
    });
  };

  const openEditModal = (session) => {
    setEditingSession(session);
    setSessionForm({
      workshop_topic_id: session.workshop_topic_id || '',
      title: session.title || '',
      scheduled_date: session.scheduled_date || '',
      scheduled_time: session.scheduled_time || '',
      duration_minutes: session.duration_minutes || 60,
      facilitator: session.facilitator || '',
      location: session.location || '',
      is_virtual: session.is_virtual !== false,
      meeting_link: session.meeting_link || '',
      max_attendees: session.max_attendees?.toString() || '',
      notes: session.notes || ''
    });
    setShowCreateModal(true);
  };

  const openAttendeeModal = (session) => {
    setSelectedSession(session);
    fetchAttendees(session.id);
    setShowAttendeeModal(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: { color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_progress: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const config = variants[status] || variants.scheduled;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const filteredSessions = sessions.filter(s => 
    s.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.facilitator?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workshop-attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Workshop Attendance</h1>
          <p className="text-muted-foreground">Schedule sessions and track attendee participation</p>
        </div>
        <Button onClick={() => { resetSessionForm(); setEditingSession(null); setShowCreateModal(true); }} data-testid="schedule-workshop-btn">
          <Plus className="w-4 h-4 mr-2" />
          Schedule Session
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.upcoming_sessions || 0}</p>
                  <p className="text-sm text-muted-foreground">Upcoming</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.completed_sessions || 0}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_attendees || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Registrations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.attendance_rate || 0}%</p>
                  <p className="text-sm text-muted-foreground">Attendance Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Sessions Quick View */}
      {stats?.recent_sessions?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Recent Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {stats.recent_sessions.slice(0, 3).map(session => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Calendar className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{session.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {session.scheduled_date} {session.scheduled_time && `at ${session.scheduled_time}`} • {session.attendee_count || 0} registered, {session.attended_count || 0} attended
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(session.status)}
                    <Button size="sm" variant="outline" onClick={() => openAttendeeModal(session)}>
                      <Users className="w-4 h-4 mr-1" />
                      Manage
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search by title or facilitator..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-sessions"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </div>

      {/* Sessions List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Workshop</th>
                  <th className="text-left p-4 font-medium">Date & Time</th>
                  <th className="text-left p-4 font-medium">Facilitator</th>
                  <th className="text-left p-4 font-medium">Attendees</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-muted-foreground">
                      No workshop sessions found. Schedule your first session!
                    </td>
                  </tr>
                ) : (
                  filteredSessions.map(session => (
                    <tr key={session.id} className="border-t hover:bg-muted/30" data-testid={`session-row-${session.id}`}>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{session.title}</p>
                          {session.location && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              {session.is_virtual ? <Link2 className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                              {session.location}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{session.scheduled_date}</span>
                          {session.scheduled_time && (
                            <>
                              <Clock className="w-4 h-4 text-muted-foreground ml-2" />
                              <span>{session.scheduled_time}</span>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{session.duration_minutes} minutes</p>
                      </td>
                      <td className="p-4">{session.facilitator || '-'}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span>{session.attendee_count || 0}</span>
                          {session.max_attendees && (
                            <span className="text-muted-foreground">/ {session.max_attendees}</span>
                          )}
                          {session.attended_count > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {session.attended_count} attended
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">{getStatusBadge(session.status)}</td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`session-actions-${session.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openAttendeeModal(session)}>
                              <Users className="w-4 h-4 mr-2" />
                              Manage Attendees
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditModal(session)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Session
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {session.status === 'scheduled' && (
                              <>
                                <DropdownMenuItem onClick={() => handleCompleteSession(session.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                  Mark Completed
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleCancelSession(session.id)}>
                                  <XCircle className="w-4 h-4 mr-2 text-red-600" />
                                  Cancel Session
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeleteSession(session.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Session Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSession ? 'Edit Session' : 'Schedule Workshop Session'}</DialogTitle>
            <DialogDescription>Fill in the details for the workshop session</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label>Workshop Topic</Label>
              <Select 
                value={sessionForm.workshop_topic_id}
                onValueChange={(value) => {
                  const topic = workshopTopics.find(t => t.id === value);
                  setSessionForm({ 
                    ...sessionForm, 
                    workshop_topic_id: value, 
                    title: topic?.title || sessionForm.title 
                  });
                }}
              >
                <SelectTrigger data-testid="workshop-topic-select">
                  <SelectValue placeholder="Select a workshop topic (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {workshopTopics.map(topic => (
                    <SelectItem key={topic.id} value={topic.id}>{topic.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Session Title *</Label>
              <Input
                placeholder="Enter session title"
                value={sessionForm.title}
                onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                data-testid="session-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={sessionForm.scheduled_date}
                  onChange={(e) => setSessionForm({ ...sessionForm, scheduled_date: e.target.value })}
                  data-testid="session-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={sessionForm.scheduled_time}
                  onChange={(e) => setSessionForm({ ...sessionForm, scheduled_time: e.target.value })}
                  data-testid="session-time"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={sessionForm.duration_minutes}
                  onChange={(e) => setSessionForm({ ...sessionForm, duration_minutes: parseInt(e.target.value) || 60 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Attendees</Label>
                <Input
                  type="number"
                  placeholder="No limit"
                  value={sessionForm.max_attendees}
                  onChange={(e) => setSessionForm({ ...sessionForm, max_attendees: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Facilitator</Label>
              <Input
                placeholder="Facilitator name"
                value={sessionForm.facilitator}
                onChange={(e) => setSessionForm({ ...sessionForm, facilitator: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={sessionForm.is_virtual}
                  onCheckedChange={(checked) => setSessionForm({ ...sessionForm, is_virtual: checked })}
                />
                <Label>Virtual Session</Label>
              </div>
            </div>
            {sessionForm.is_virtual ? (
              <div className="space-y-2">
                <Label>Meeting Link</Label>
                <Input
                  placeholder="https://zoom.us/j/..."
                  value={sessionForm.meeting_link}
                  onChange={(e) => setSessionForm({ ...sessionForm, meeting_link: e.target.value })}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  placeholder="Physical location"
                  value={sessionForm.location}
                  onChange={(e) => setSessionForm({ ...sessionForm, location: e.target.value })}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={sessionForm.notes}
                onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={editingSession ? handleUpdateSession : handleCreateSession} data-testid="save-session-btn">
              {editingSession ? 'Update Session' : 'Schedule Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendees Modal */}
      <Dialog open={showAttendeeModal} onOpenChange={setShowAttendeeModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Attendees</DialogTitle>
            {selectedSession && (
              <DialogDescription>
                {selectedSession.title} • {selectedSession.scheduled_date} {selectedSession.scheduled_time && `at ${selectedSession.scheduled_time}`}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {/* Attendee Stats */}
          {attendees.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-xl font-bold text-blue-700">{attendees.length}</p>
                <p className="text-xs text-blue-600">Total</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-xl font-bold text-green-700">{attendees.filter(a => a.attended).length}</p>
                <p className="text-xs text-green-600">Attended</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-xl font-bold text-red-700">{attendees.filter(a => !a.attended).length}</p>
                <p className="text-xs text-red-600">Not Attended</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mb-4">
            <Button onClick={() => setShowAddAttendeeModal(true)} data-testid="add-attendee-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Attendee
            </Button>
          </div>

          {/* Attendees List */}
          <div className="space-y-2">
            {attendees.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No attendees registered yet. Add the first one!
              </div>
            ) : (
              attendees.map(attendee => (
                <div key={attendee.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`attendee-${attendee.id}`}>
                  <div className="flex-1">
                    <p className="font-medium">{attendee.name}</p>
                    <p className="text-sm text-muted-foreground">{attendee.email}</p>
                    {attendee.organization && (
                      <p className="text-xs text-muted-foreground">{attendee.organization}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={attendee.attended ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {attendee.attended ? 'Attended' : 'Registered'}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!attendee.attended && (
                          <DropdownMenuItem onClick={() => handleCheckIn(selectedSession.id, attendee.id)}>
                            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                            Check In
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleRemoveAttendee(selectedSession.id, attendee.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Attendee Modal */}
      <Dialog open={showAddAttendeeModal} onOpenChange={setShowAddAttendeeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Attendee</DialogTitle>
            <DialogDescription>Register a new attendee for this session</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Full name"
                value={attendeeForm.name}
                onChange={(e) => setAttendeeForm({ ...attendeeForm, name: e.target.value })}
                data-testid="attendee-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={attendeeForm.email}
                onChange={(e) => setAttendeeForm({ ...attendeeForm, email: e.target.value })}
                data-testid="attendee-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                placeholder="Phone number"
                value={attendeeForm.phone}
                onChange={(e) => setAttendeeForm({ ...attendeeForm, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Organization</Label>
              <Input
                placeholder="Company or organization"
                value={attendeeForm.organization}
                onChange={(e) => setAttendeeForm({ ...attendeeForm, organization: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAttendeeModal(false)}>Cancel</Button>
            <Button onClick={handleAddAttendee} data-testid="save-attendee-btn">Add Attendee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWorkshopAttendance;
